import { useMemo, useState } from 'react';

import { Avatar, Btn } from '@/components/ui';
import { NavIcon } from '@/components/icons/NavIcon';
import { useEmailSuggestions } from '@/hooks';
import type { Level } from '@/api/types';
import { emailLocal, levelLabel } from '@/lib/format';

import styles from './PermTab.module.css';

/* ─────────────────────────────────────────────────────
 *  AddMemberForm — 이메일 검색 후 여러 명을 누적 선택해 한 번에 부여.
 *  - useEmailSuggestions로 server-side 검색.
 *  - 선택한 이메일은 검색창 아래 칩으로 쌓이고, 검색 결과에서 제외된다.
 *  - 이미 멤버이거나 이미 선택한 이메일은 다시 추가 불가.
 *  - admin: 사유 옵셔널. 비admin: 사유 필수.
 * ───────────────────────────────────────────────────── */
interface AddMemberFormProps {
  existingEmails: string[];
  isAdmin: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
  // 누적 선택된 이메일 전체를 같은 역할/사유로 한 번에 부여.
  onSubmit: (emails: string[], level: Level, reason: string) => void;
}

export default function AddMemberForm({ existingEmails, isAdmin, isSubmitting, onCancel, onSubmit }: AddMemberFormProps) {
  const [searchQ, setSearchQ] = useState('');
  const [pickedEmails, setPickedEmails] = useState<string[]>([]);
  const [role, setRole] = useState<Level>('viewer');
  const [reason, setReason] = useState('');
  const suggestion = useEmailSuggestions(searchQ);

  const existing = useMemo(() => new Set(existingEmails), [existingEmails]);
  const picked = useMemo(() => new Set(pickedEmails), [pickedEmails]);
  // 이미 멤버이거나 이미 선택한 이메일은 검색 결과에서 제외.
  const filtered = useMemo(
    () => suggestion.results.filter((u) => !existing.has(u.email) && !picked.has(u.email)),
    [suggestion.results, existing, picked],
  );

  // 검색 결과에 없는 이메일도 직접 입력해 추가 가능. 도메인 검증은 backend가 정책으로 강제.
  const trimmedQ = searchQ.trim().toLowerCase();
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedQ);
  const isExactMatchInResults = filtered.some((u) => u.email.toLowerCase() === trimmedQ);
  const alreadyMember = existing.has(trimmedQ);
  const alreadyPicked = picked.has(trimmedQ);
  const canAddManually = looksLikeEmail && !isExactMatchInResults && !alreadyMember && !alreadyPicked;

  // 선택 추가 — 누적. 검색어는 비워 다음 검색을 바로 이어갈 수 있게 한다.
  const addEmail = (email: string) => {
    setPickedEmails((prev) => (prev.includes(email) ? prev : [...prev, email]));
    setSearchQ('');
  };
  const removeEmail = (email: string) => {
    setPickedEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleSubmit = () => {
    if (pickedEmails.length === 0) return;
    if (!isAdmin && !reason.trim()) return;
    onSubmit(pickedEmails, role, reason);
  };

  const canSubmit = pickedEmails.length > 0 && (isAdmin || reason.trim().length > 0) && !isSubmitting;
  const count = pickedEmails.length;
  const submitLabel = isSubmitting
    ? '적용 중...'
    : isAdmin
      ? count > 1
        ? `${count}명 추가하기`
        : '추가하기'
      : count > 1
        ? `${count}명 추가 요청`
        : '추가 요청 보내기';

  return (
    <div className={styles.addForm}>
      <div className={styles.addFormGrid}>
        {/* 멤버 선택 — 검색 후 누적 */}
        <div>
          <div className={styles.addFormLabel}>
            멤버 선택 <span className={styles.addFormRequired}>*</span>
            {count > 0 && <span className={styles.pickedCount}>{count}명 선택됨</span>}
          </div>
          <div className={styles.memberPickerBox}>
            <div className={styles.memberPickerSearch}>
              <NavIcon name="search" color="#9B9B9B" size={14} />
              <input
                className={styles.memberPickerSearchInput}
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="이메일로 검색해 추가"
                autoFocus
              />
            </div>
            <div className={styles.memberPickerList}>
              {!searchQ.trim() && (
                <div className={styles.memberPickerEmpty}>이메일을 입력해 검색해주세요</div>
              )}
              {searchQ.trim() && suggestion.loading && (
                <div className={styles.memberPickerEmpty}>검색 중...</div>
              )}
              {searchQ.trim() && !suggestion.loading && filtered.length === 0 && !canAddManually && (
                <div className={styles.memberPickerEmpty}>
                  {alreadyMember
                    ? '이미 멤버로 등록돼 있어요'
                    : alreadyPicked
                      ? '이미 선택한 이메일이에요'
                      : looksLikeEmail
                        ? '검색 결과가 없어요'
                        : '이메일 형식으로 입력해주세요'}
                </div>
              )}
              {canAddManually && (
                <button
                  type="button"
                  className={styles.memberPickerItem}
                  onClick={() => addEmail(trimmedQ)}
                  style={{ borderTop: filtered.length > 0 ? '1px solid #F4F4F4' : undefined }}
                >
                  <Avatar email={trimmedQ} name={emailLocal(trimmedQ)} size={28} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={styles.memberPickerItemName}>+ 이 이메일로 추가</div>
                    <div className={styles.memberPickerItemEmail}>{trimmedQ}</div>
                  </div>
                </button>
              )}
              {filtered.map((u) => (
                <button
                  key={u.email}
                  type="button"
                  className={styles.memberPickerItem}
                  onClick={() => addEmail(u.email)}
                >
                  <Avatar email={u.email} name={emailLocal(u.email)} size={28} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={styles.memberPickerItemName}>{emailLocal(u.email)}</div>
                    <div className={styles.memberPickerItemEmail}>{u.email}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* 선택된 멤버 칩 — 누적, 개별 제거 가능 */}
          {count > 0 && (
            <div className={styles.pickedChips}>
              {pickedEmails.map((email) => (
                <span key={email} className={styles.pickedChip}>
                  <Avatar email={email} name={emailLocal(email)} size={20} />
                  <span className={styles.pickedChipName}>{emailLocal(email)}</span>
                  <button
                    type="button"
                    className={styles.pickedChipRemove}
                    onClick={() => removeEmail(email)}
                    aria-label={`${email} 제거`}
                  >
                    <NavIcon name="x" size={10} color="currentColor" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* 역할 라디오 — 선택한 모든 멤버에 공통 적용 */}
        <div>
          <div className={styles.addFormLabel}>
            역할 <span className={styles.addFormRequired}>*</span>
          </div>
          <div className={styles.roleRadioGroup}>
            {(['editor', 'viewer'] as Level[]).map((r) => (
              <button
                key={r}
                type="button"
                className={`${styles.roleRadio} ${role === r ? styles.on : ''}`}
                onClick={() => setRole(r)}
              >
                <span className={styles.roleRadioDot}>
                  {role === r && <span className={styles.roleRadioDotInner} />}
                </span>
                <span>{levelLabel(r)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 사유 */}
      <div>
        <div className={styles.reasonLabel}>
          요청 사유 {!isAdmin && <span className={styles.addFormRequired}>*</span>}
        </div>
        <textarea
          className={styles.reasonField}
          rows={2}
          placeholder={isAdmin ? '메모로 남길 사유 (선택)' : '이 멤버를 추가해야 하는 이유'}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>

      <div className={styles.addFormActions}>
        <Btn variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          취소
        </Btn>
        <Btn
          variant="primary"
          icon={<NavIcon name="plus" size={14} color="currentColor" />}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitLabel}
        </Btn>
      </div>
    </div>
  );
}
